import {
	camelCase, forOwn, isPlainObject,
} from 'lodash';

const snakeCase = (string) => {
	return string.split(/ |\B(?=[A-Z])/)
		.map((word) => word.toLowerCase())
		.join('_');
};

/**
 * Like Lodash's mapKeys, except this recursively calls it on subobjects as well.
 * Transforms all keys (and subkeys) of an object according to what the callback returns.
 * All values remain unchanged.
 *
 * @param {object|Array} obj - The object to transform the keys of
 * @param {Function} callback - The callback to apply to each key, called with (value, key)
 * @returns {object} - The object with its keys transformed according to what the callback returned
 */
const deepMapKeys = (obj, callback) => {
	if (Array.isArray(obj)) {
		return obj.map((value) => deepMapKeys(value, callback));
	} else if (typeof obj === 'object') {
		const x = {
		};

		forOwn(obj, (value, key) => {
			if (isPlainObject(value)) {
				x[callback(value, key)] = deepMapKeys(value, callback);
			} else if (Array.isArray(value)) {
				x[callback(value, key)] = value.map(
					(item) => typeof item === 'object' ? deepMapKeys(item, callback) : item
				);
			} else {
				x[callback(value, key)] = value;
			}
		});
		return x;
	}
	return obj;
};

/**
 * Transforms all keys of an object to match their snake_case equivalent. Useful for when sending
 * an Ajax request to our back-end that expects snake_case rather than camelCase
 *
 * @param {object} params - The object for which we want to transform its keys.
 * @returns {object} - The object with all of its keys converted to their snake_case equivalent
 */
export const keysToSnakeCase = (params) => deepMapKeys(params, (value, key) => snakeCase(key));

/**
 * Transforms all keys of an object to match their camelCase equivalent
 *
 * @param {object} params - The object for which we want to transform its keys.
 * @returns {object} - The object with all of its keys converted to their camelCase equivalent
 */
export const keysToCamelCase = (params) => deepMapKeys(params, (value, key) => camelCase(key));

/**
 * Removes null and undefined values from an object
 *
 * @param {object} obj - The JSON object to filter
 * @returns {object} - The filtered object
 */
export const sanitize = (obj) => {
	Object.entries(obj).forEach(([key, val, ]) =>
		(val && typeof val === 'object') && sanitize(val) ||
		(val === null || val === '' || val === undefined) && delete obj[key]
	);
	return obj;
};

